import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiRefreshCw, FiUnlock } from "react-icons/fi";
import { toast } from "react-toastify";
import {
  fetchTimesheetDashboardUsers,
  fetchTimesheetReopenStatus,
  reopenTimesheetPeriod,
} from "../../services/timesheetApi";
import { getBangkokDateKey, getEntityId, getWeekRange } from "../../utils/timesheetUtils";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime())
    ? "-"
    : date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};

const addCalendarDays = (dateKey, days) => {
  const [year, month, day] = dateKey.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

const DATE_VALIDATION_MESSAGE = "วันที่เปิดให้ถึงต้องเป็นวันนี้หรือวันถัดไป";

const getErrorMessage = (error, fallback) => {
  const status = error?.response?.status;
  const backendMessage = String(error?.response?.data?.message || "");
  if (backendMessage === "reopenUntil cannot be before today in Asia/Bangkok") {
    return DATE_VALIDATION_MESSAGE;
  }
  if (status === 403) return "คุณไม่มีสิทธิ์เปิด Timesheet ย้อนหลัง";
  if (status === 409) {
    return error?.response?.data?.message || "ช่วงเวลานี้ยังไม่สามารถเปิดย้อนหลังได้";
  }
  return error?.response?.data?.message || fallback;
};

const TimesheetReopenPage = () => {
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [employeeQuery, setEmployeeQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [reopenUntil, setReopenUntil] = useState("");
  const [reason, setReason] = useState("");
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const weekDateInputRef = useRef(null);
  const reopenUntilInputRef = useRef(null);
  const todayKey = useMemo(() => getBangkokDateKey(), []);

  const selectedEmployee = useMemo(
    () => employees.find((employee) => getEntityId(employee._id || employee.id) === selectedUserId),
    [employees, selectedUserId]
  );

  const filteredEmployees = useMemo(() => {
    const query = employeeQuery.trim().toLowerCase();
    if (!query || selectedUserId) return [];
    return employees
      .filter((employee) => {
        const values = [employee.username, employee.email, employee.department, employee.team];
        return values.some((value) => String(value || "").toLowerCase().includes(query));
      })
      .slice(0, 8);
  }, [employeeQuery, employees, selectedUserId]);

  const weekRange = useMemo(
    () => (periodStart ? getWeekRange(new Date(`${periodStart}T12:00:00`)) : null),
    [periodStart]
  );

  const loadEmployees = useCallback(async () => {
    try {
      setEmployeesLoading(true);
      const response = await fetchTimesheetDashboardUsers();
      setEmployees(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error, "ไม่สามารถโหลดรายชื่อพนักงานได้"));
      setEmployees([]);
    } finally {
      setEmployeesLoading(false);
    }
  }, []);

  const loadStatus = useCallback(async () => {
    if (!selectedUserId || !periodStart) {
      setStatus(null);
      setStatusError("");
      return;
    }

    try {
      setStatusLoading(true);
      setStatusError("");
      setStatus(await fetchTimesheetReopenStatus({ userId: selectedUserId, periodStart }));
    } catch (error) {
      setStatus(null);
      setStatusError(getErrorMessage(error, "ไม่สามารถโหลดสถานะการเปิดย้อนหลังได้"));
    } finally {
      setStatusLoading(false);
    }
  }, [periodStart, selectedUserId]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (status?.isExpired && !reopenUntil) {
      setReopenUntil(addCalendarDays(todayKey, 7));
    }
  }, [reopenUntil, status?.isExpired, todayKey]);

  const selectEmployee = (employee) => {
    setSelectedUserId(getEntityId(employee._id || employee.id));
    setEmployeeQuery(employee.username || employee.email || "");
  };

  const handleWeekChange = (event) => {
    const value = event.target.value;
    if (!value) {
      setPeriodStart("");
      return;
    }
    setPeriodStart(getWeekRange(new Date(`${value}T12:00:00`)).from);
  };

  const openDatePicker = (inputRef) => {
    const input = inputRef.current;
    if (!input) return;

    input.focus();
    try {
      input.showPicker?.();
    } catch {
      // Some mobile browsers only allow the native picker from a direct tap.
    }
  };

  const handleReopen = async (event) => {
    event.preventDefault();
    if (!selectedUserId) {
      toast.error("กรุณาเลือกพนักงาน");
      return;
    }
    if (!periodStart) {
      toast.error("กรุณาเลือกสัปดาห์ที่ต้องการเปิด");
      return;
    }
    if (!reopenUntil) {
      toast.error("กรุณาระบุวันที่เปิดให้ถึง");
      return;
    }
    if (reopenUntil < todayKey) {
      toast.error(DATE_VALIDATION_MESSAGE);
      return;
    }
    if (!reason.trim()) {
      toast.error("กรุณาระบุเหตุผล");
      return;
    }
    if (!status?.isExpired || ["pending", "approved"].includes(String(status?.lockReason || "").toLowerCase())) {
      toast.error(
        status?.lockReason === "pending" || status?.lockReason === "approved"
          ? "ช่วงเวลานี้ถูกล็อกจากสถานะการอนุมัติ ไม่สามารถเปิดย้อนหลังได้"
          : "ช่วงเวลานี้ยังไม่เกินกำหนด"
      );
      return;
    }

    try {
      setSubmitting(true);
      await reopenTimesheetPeriod({
        userId: selectedUserId,
        periodStart,
        reopenUntil,
        reason: reason.trim(),
      });
      toast.success("เปิด Timesheet ย้อนหลังสำเร็จ");
      setReason("");
      await loadStatus();
    } catch (error) {
      toast.error(getErrorMessage(error, "ไม่สามารถเปิด Timesheet ย้อนหลังได้"));
    } finally {
      setSubmitting(false);
    }
  };

  const approvalLocked = ["pending", "approved"].includes(
    String(status?.lockReason || "").toLowerCase()
  );
  const canReopen = Boolean(status?.isExpired) && !approvalLocked && !status?.isReopened;

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-5 transition-colors duration-300 dark:bg-gray-900 sm:px-6">
      <div className="rounded-3xl border border-indigo-100 bg-gradient-to-br from-white via-indigo-50/50 to-sky-50/70 p-6 shadow-sm dark:border-indigo-900/40 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300">
              <FiUnlock size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 sm:text-3xl">
                Timesheet - เปิดย้อนหลัง
              </h1>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                เปิดให้พนักงานแก้ไขและส่ง Timesheet ที่เกินกำหนดแล้ว
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={loadEmployees}
            disabled={employeesLoading}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
          >
            <FiRefreshCw className={employeesLoading ? "animate-spin" : ""} />
            รีเฟรช
          </button>
        </div>
      </div>

      <form onSubmit={handleReopen} className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr),minmax(320px,0.8fr)]">
        <div className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
          <div className="grid gap-5">
            <div className="relative">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">พนักงาน</label>
              <input
                value={employeeQuery}
                onChange={(event) => {
                  setEmployeeQuery(event.target.value);
                  setSelectedUserId("");
                }}
                placeholder={employeesLoading ? "กำลังโหลดรายชื่อ..." : "ค้นหาชื่อผู้ใช้"}
                className="mt-2 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-indigo-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
              />
              {filteredEmployees.length > 0 && (
                <div className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                  {filteredEmployees.map((employee) => (
                    <button
                      key={getEntityId(employee._id || employee.id)}
                      type="button"
                      onClick={() => selectEmployee(employee)}
                      className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
                    >
                      <span className="block font-medium text-gray-900 dark:text-gray-100">
                        {employee.username || employee.email || "-"}
                      </span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        {[employee.department, employee.team].filter(Boolean).join(" • ") || "-"}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {selectedEmployee && (
                <p className="mt-2 text-xs text-indigo-600 dark:text-indigo-300">
                  เลือกแล้ว: {selectedEmployee.username || selectedEmployee.email}
                </p>
              )}
            </div>

            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              สัปดาห์ที่ต้องการเปิด
              <div className="mt-2 w-full cursor-pointer" onClick={() => openDatePicker(weekDateInputRef)}>
                <input
                  ref={weekDateInputRef}
                  type="date"
                  value={periodStart}
                  onChange={handleWeekChange}
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-indigo-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                />
              </div>
              <span className="mt-1 block text-xs font-normal text-red-600 dark:text-red-400">
                เลือกวันใดก็ได้ในสัปดาห์ ระบบจะกำหนดวันจันทร์เป็นวันเริ่มสัปดาห์ให้อัตโนมัติ
              </span>
            </label>

            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              เปิดให้บันทึกได้ถึงวันที่
              <div className="mt-2 w-full cursor-pointer" onClick={() => openDatePicker(reopenUntilInputRef)}>
                <input
                  ref={reopenUntilInputRef}
                  type="date"
                  value={reopenUntil}
                  min={todayKey}
                  onChange={(event) => setReopenUntil(event.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-indigo-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                />
              </div>
            </label>

            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              เหตุผล
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={4}
                placeholder="เช่น อนุมัติให้ส่ง Timesheet ย้อนหลัง"
                className="mt-2 w-full resize-y rounded-xl border border-gray-200 bg-white p-3 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-indigo-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
              />
            </label>

            <button
              type="submit"
              disabled={submitting || statusLoading || !canReopen}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FiUnlock />
              {submitting ? "กำลังเปิดย้อนหลัง..." : "เปิดให้แก้ไขย้อนหลัง"}
            </button>
          </div>
        </div>

        <div className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">สถานะช่วงเวลา</h2>
          {!selectedUserId || !periodStart ? (
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">เลือกพนักงานและสัปดาห์เพื่อดูสถานะ</p>
          ) : statusLoading ? (
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">กำลังโหลดสถานะ...</p>
          ) : statusError ? (
            <p className="mt-4 text-sm font-medium text-red-600 dark:text-red-300">{statusError}</p>
          ) : status ? (
            <div className="mt-4 space-y-3 text-sm">
              <p>สัปดาห์: <strong>{weekRange ? `${formatDate(weekRange.from)} - ${formatDate(weekRange.to)}` : "-"}</strong></p>
              <p>กำหนดส่ง: <strong>{formatDate(status.deadline)}</strong></p>
              <p>สถานะ: <strong>{approvalLocked ? "ถูกล็อกจากสถานะการอนุมัติ" : status.isExpired ? "หมดเวลาส่ง" : "ยังไม่เกินกำหนด"}</strong></p>
              {status.isReopened && (
                <div className="rounded-xl bg-indigo-50 p-3 text-indigo-800 dark:bg-indigo-500/10 dark:text-indigo-200">
                  <p className="font-semibold">เปิดให้แก้ไขย้อนหลังอยู่</p>
                  <p className="mt-1">เปิดถึง: {formatDate(status.reopenUntil)}</p>
                  {status.reason && <p className="mt-1 break-words">เหตุผล: {status.reason}</p>}
                  {status.reopenedBy && <p className="mt-1">เปิดโดย: {status.reopenedBy.username || status.reopenedBy.email || status.reopenedBy}</p>}
                </div>
              )}
              {approvalLocked && (
                <p className="rounded-xl bg-amber-50 p-3 text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
                  ช่วงเวลานี้ถูกล็อกจากสถานะการอนุมัติ การเปิดย้อนหลังไม่สามารถปลดล็อกสถานะอนุมัติได้
                </p>
              )}
              {!status.isExpired && !approvalLocked && (
                <p className="rounded-xl bg-gray-50 p-3 text-gray-600 dark:bg-gray-900/40 dark:text-gray-300">
                  ช่วงเวลานี้ยังไม่เกินกำหนด จึงไม่จำเป็นต้องเปิดย้อนหลัง
                </p>
              )}
            </div>
          ) : null}
        </div>
      </form>
    </div>
  );
};

export default TimesheetReopenPage;
